import {
  BottomSheet,
  Illustration,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { environmentConfig } from "@src/appV2/environment";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { writeToClipboard } from "@src/appV2/NativeAdapters/clipboard.adapter";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useCallback } from "react";

import { BottomSheetHeader } from "../../../../redesign/components/BottomSheetHeader";
import { getWorkplaceSignUpWithReferralCodePath } from "../../api/utils";
import {
  WorkplaceReferralAction,
  WorkplaceReferralActionSource,
  WorkplaceReferralComponent,
} from "../../types";
import { shareReferralLink } from "./shareLink";

interface ShareReferralLinkBottomSheetProps {
  modalState: UseModalState;
  referralCode: string;
}

export function ShareReferralLinkBottomSheet(props: ShareReferralLinkBottomSheetProps) {
  const { modalState, referralCode } = props;
  const { showSuccessToast } = useToast();
  const signUpReferralCodeLink = `${
    environmentConfig.REACT_APP_ADMIN_WEB_APP_URL
  }${getWorkplaceSignUpWithReferralCodePath(referralCode)}`;

  const handleClose = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.WORKPLACE_REFERRAL, {
      action: WorkplaceReferralAction.SHARE_REFERRAL_LINK_DIALOG_CLOSED,
      component: WorkplaceReferralComponent.SHARE_REFERRAL_LINK_DIALOG,
      referralCode,
      source: WorkplaceReferralActionSource.BUTTON_CLICKED,
    });
    modalState.closeModal();
  }, [modalState, referralCode]);

  const handleCopyLink = useCallback(async () => {
    await writeToClipboard(signUpReferralCodeLink);
    logEvent(APP_V2_USER_EVENTS.WORKPLACE_REFERRAL, {
      action: WorkplaceReferralAction.REFERRAL_LINK_COPIED,
      component: WorkplaceReferralComponent.SHARE_REFERRAL_LINK_DIALOG,
      referralCode,
      link: signUpReferralCodeLink,
    });
    showSuccessToast("Referral link copied successfully");
  }, [referralCode, showSuccessToast, signUpReferralCodeLink]);

  const handleShareLink = useCallback(async () => {
    await shareReferralLink({
      signUpReferralCodeLink,
      referralCode,
    });
  }, [referralCode, signUpReferralCodeLink]);

  return (
    <BottomSheet
      canDismiss
      modalState={modalState}
      header={<BottomSheetHeader onClose={handleClose} />}
    >
      <Stack
        spacing={6}
        sx={{
          paddingX: 5,
          paddingTop: 4,
          paddingBottom: 2,
          textAlign: "center",
          alignItems: "center",
        }}
      >
        <Illustration type="request-sent" />

        <Stack spacing={4}>
          <Title component="h2" textAlign="center">
            Invite workplace
          </Title>

          <Text variant="body1" textAlign="center">
            Share this link with a workplace and get rewarded once their first shift is completed
          </Text>
        </Stack>
      </Stack>

      <DialogFooter orientation="vertical">
        <Button fullWidth variant="contained" size="large" onClick={handleCopyLink}>
          Copy link
        </Button>
        <Button fullWidth variant="outlined" size="large" onClick={handleShareLink}>
          Share link
        </Button>
      </DialogFooter>
    </BottomSheet>
  );
}
